import { useEffect } from "react";
import * as THREE from "three";
import { CSS2DObject } from "three-stdlib";
import { getConstraintDisplayName } from "../../utils/constraintNames";
import {
  createManagedLabelElement,
  CONSTRAINT_LABEL_CLASS,
  applyLabelStyles,
  JOINT_LABEL_ACTUATOR_ICON_CLASS,
  applyJointLabelLayoutStyles,
  JOINT_LABEL_TEXT_CLASS,
} from "../labelUtils";
import {
  applyMarkerLabelCss2dLayout,
  CONSTRAINT_MARKER_LABEL_LIFT_LOCAL_METERS,
} from "../coincidentLabelSpread";
import { disposeSceneObjectTree } from "../scene/sceneDispose";
import { makeDashedLine2, makeSolidLine2 } from "../scene/lineFactories";
import {
  buildViewerSceneRouteObjectIndex,
  collectSpatialTendonRouteObjects,
  findJointVizGroupInScene,
} from "../scene/sceneObjectLookups";
import {
  applyConstraintMarkerKeySelection,
  syncConstraintMarkerCss2dLabelVisibility,
} from "../scene/constraintMarkerVisibility";
import { selectionFocusBodiesMultiSnapshot } from "../scene/sceneFocusDim";
import {
  computeConstraintMarkerScaleFromModelGroup,
  getConstraintMarkerSettingsScales,
} from "../scene/jointMarkerSizing";
import { TENDON_COLORS, getTendonVisualizationStyle } from "../scene/tendonStyle";
import {
  buildTendonCurvePoints,
  canonicalJointPairKey,
  jointPairsWithCoincidentTendonAndJointEq,
  getConnectBodyAnchorState,
  getConnectBodyAnchorWorldPositions,
  getConnectBodyDisplayLinePoints,
  setJointPivotWorldFromViz,
  COINCIDENT_JOINT_EQ_ARC_BOW_SCALE,
  COINCIDENT_TENDON_ARC_BOW_SCALE,
} from "../scene/constraintMarkerGeometry";
import { preferTendonConstraintMarkerHit } from "../scene/scenePicking";
import {
  ACTUATED_JOINT_LABEL_ICON_COLOR,
  CONNECT_CONSTRAINT_GAP_VISIBLE_METERS,
  CONNECT_CONSTRAINT_GAP_WARN_METERS,
  CONSTRAINT_CONNECT_BODY1_MARKER_COLOR,
  CONSTRAINT_CONNECT_BODY2_MARKER_COLOR,
  CONSTRAINT_CONNECT_MARKER_COLOR,
  CONSTRAINT_JOINT_EQ_MARKER_COLOR,
  CONSTRAINT_MARKER_DASHED_LINE_WIDTH,
  CONSTRAINT_MARKER_JOINT_EQ_LINE_WIDTH,
  CONSTRAINT_MARKER_MIN_LINE_SCALE,
  CONSTRAINT_MARKER_RADIUS,
  CONSTRAINT_MARKER_RADIUS_LARGE,
  CONSTRAINT_MARKER_RADIUS_SMALL,
  CONSTRAINT_MARKER_SOLID_LINE_WIDTH,
  CONSTRAINT_MARKER_WELD_BOX_SIZE,
  CONSTRAINT_MARKER_WELD_PICK_RADIUS,
  CONSTRAINT_WELD_MARKER_COLOR,
} from "../scene/sceneConstants";

export function useConstraintMarkers({
  sceneRef,
  cameraRef,
  rendererRef,
  sceneObjectsRef,
  jointObjectsRef,
  constraintMarkersGroupRef,
  constraintMarkerMeshesRef,
  constraintHoveredKeyRef,
  constraintMarkerLayerStateRef,
  constraintPointerRef,
  constraintMarkerBuildRetryCountRef,
  constraintMarkerSettings,
  constraintMarkerLayerVisible,
  tendonMarkerLayerVisible,
  constraintVisibilityUi,
  selectedEquality,
  selectedTendon,
  allEqualities,
  allTendons,
  showConstraintMarkers,
  focusModeActive,
  focusBodyUuid,
  focusMeshUuid,
  focusCollisionGeomUuid,
  selectedBodiesMultiKey,
  structureChangeTick,
  constraintMarkersRetryNonce,
  setConstraintMarkersRetryNonce,
}) {
  // Always-on constraint/tendon markers (pill menu toggle).
  // Shows color-coded markers at all anchor/joint positions; connecting line/arc appears on hover.
  useEffect(() => {
    if (!sceneRef.current) return;

    let group = constraintMarkersGroupRef.current;
    if (!group) {
      group = new THREE.Group();
      group.name = "constraint_markers";
      sceneRef.current.add(group);
      constraintMarkersGroupRef.current = group;
    }

    const clearGroupChildren = () => {
      while (group.children.length > 0) {
        const child = group.children[0];
        disposeSceneObjectTree(child);
        group.remove(child);
      }
    };

    clearGroupChildren();
    constraintMarkerMeshesRef.current = [];
    constraintHoveredKeyRef.current = null;
    const hasSelectedConstraint = !!selectedEquality || !!selectedTendon;
    const anyConstraintMarkerLayerVisible =
      constraintMarkerLayerVisible || tendonMarkerLayerVisible;
    group.visible = anyConstraintMarkerLayerVisible || hasSelectedConstraint;

    let retryTimeout = null;
    let rafId = null;

    if (!anyConstraintMarkerLayerVisible && !hasSelectedConstraint) {
      constraintMarkerBuildRetryCountRef.current = 0;
      return () => {
        if (retryTimeout) clearTimeout(retryTimeout);
        if (rafId !== null) cancelAnimationFrame(rafId);
        constraintHoveredKeyRef.current = null;
        constraintMarkerMeshesRef.current = [];
        clearGroupChildren();
        const g = constraintMarkersGroupRef.current;
        if (g && g.parent) g.parent.remove(g);
        constraintMarkersGroupRef.current = null;
      };
    }

    const CONNECT_COLOR = CONSTRAINT_CONNECT_MARKER_COLOR;
    const WELD_COLOR = CONSTRAINT_WELD_MARKER_COLOR;
    const connectGapOkColor = new THREE.Color(CONNECT_COLOR);
    const connectGapWarnColor = new THREE.Color(0xff2200);

    const visibleEqualitiesForMarkers =
      constraintMarkerLayerVisible || selectedEquality
        ? Object.values(allEqualities || {}).filter(
            (eq) => constraintMarkerLayerVisible || eq?.uuid === selectedEquality
          )
        : [];
    const visibleTendonsForMarkers =
      tendonMarkerLayerVisible || selectedTendon
        ? Object.values(allTendons || {}).filter(
            (tendon) => tendonMarkerLayerVisible || tendon?.uuid === selectedTendon
          )
        : [];
    const coincidentTendonJointEqPairs = jointPairsWithCoincidentTendonAndJointEq(
      Object.fromEntries(visibleTendonsForMarkers.map((tendon) => [tendon.uuid, tendon])),
      Object.fromEntries(visibleEqualitiesForMarkers.map((eq) => [eq.uuid, eq]))
    );

    /** Coincident tendon + joint-eq endpoints: large spheres drawn first (behind), small on top. */
    const MARKER_SPHERE_RENDER_LARGE_BACK = 9995;
    const MARKER_SPHERE_RENDER_BASE = 9996;
    const MARKER_SPHERE_RENDER_SMALL_TOP = 9999;

    /** Fat-line arcs when tendon + joint equality share joints: equality behind tendon arc + tendon spheres. */
    const MARKER_ARC_RENDER_JOINT_EQ_COINCIDENT_BACK = 9993;
    const MARKER_ARC_RENDER_NORMAL = 9996;
    const MARKER_ARC_RENDER_TENDON_OVER_EQ = 9997;
    const modelGroup = sceneRef.current.getObjectByName("modelGroup");
    const constraintMarkerScales = getConstraintMarkerSettingsScales(constraintMarkerSettings);
    const modelMarkerScale = computeConstraintMarkerScaleFromModelGroup(modelGroup);
    const markerScale = modelMarkerScale * constraintMarkerScales.markerScale;
    const lineScale = Math.max(modelMarkerScale, CONSTRAINT_MARKER_MIN_LINE_SCALE);
    const lineWidthScale = constraintMarkerScales.lineWidthScale;
    const dashedLineWidth = CONSTRAINT_MARKER_DASHED_LINE_WIDTH * lineScale * lineWidthScale;
    const solidLineWidth = CONSTRAINT_MARKER_SOLID_LINE_WIDTH * lineScale * lineWidthScale;
    const jointEqLineWidth = CONSTRAINT_MARKER_JOINT_EQ_LINE_WIDTH * lineScale * lineWidthScale;
    const weldBoxSize = CONSTRAINT_MARKER_WELD_BOX_SIZE * markerScale;

    // Shared geometries — reused across marker spheres of each size class
    const markerGeo = new THREE.SphereGeometry(CONSTRAINT_MARKER_RADIUS * markerScale, 10, 10);
    const markerGeoLarge = new THREE.SphereGeometry(
      CONSTRAINT_MARKER_RADIUS_LARGE * markerScale,
      10,
      10
    );
    const markerGeoLargeSparseWire = new THREE.SphereGeometry(
      CONSTRAINT_MARKER_RADIUS_LARGE * markerScale,
      8,
      4
    );
    const markerGeoSmall = new THREE.SphereGeometry(
      CONSTRAINT_MARKER_RADIUS_SMALL * markerScale,
      10,
      10
    );
    const weldPickGeo = new THREE.SphereGeometry(
      CONSTRAINT_MARKER_WELD_PICK_RADIUS * markerScale,
      6,
      6
    );

    // Helper: add a visible marker sphere + register it as a raycast target
    const addMarkerSphere = (pos, color, key, extraData = {}) => {
      const {
        markerSphereGeometry = markerGeo,
        markerRenderOrder = MARKER_SPHERE_RENDER_BASE,
        ...markerRest
      } = extraData;
      const sphere = new THREE.Mesh(
        markerSphereGeometry,
        new THREE.MeshBasicMaterial({ color, depthTest: false, depthWrite: false })
      );
      sphere.material.visible = constraintMarkerScales.anchorSpheresVisible;
      sphere.position.copy(pos);
      sphere.renderOrder = markerRenderOrder;
      sphere.userData = {
        isConstraintVisualization: true,
        isConstraintMarker: true,
        key,
        ...markerRest,
      };
      group.add(sphere);
      constraintMarkerMeshesRef.current.push(sphere);
      return sphere;
    };

    const addWireMarkerSphere = (pos, color, key, extraData = {}) => {
      const {
        markerSphereGeometry = markerGeo,
        markerRenderOrder = MARKER_SPHERE_RENDER_BASE,
        markerOpacity = 1,
        ...markerRest
      } = extraData;
      const sphere = new THREE.Mesh(
        markerSphereGeometry,
        new THREE.MeshBasicMaterial({
          color,
          depthTest: false,
          depthWrite: false,
          transparent: markerOpacity < 1,
          opacity: markerOpacity,
          wireframe: true,
        })
      );
      sphere.material.visible = constraintMarkerScales.anchorSpheresVisible;
      sphere.position.copy(pos);
      sphere.renderOrder = markerRenderOrder;
      sphere.userData = {
        isConstraintVisualization: true,
        isConstraintMarker: true,
        key,
        ...markerRest,
      };
      group.add(sphere);
      constraintMarkerMeshesRef.current.push(sphere);
      return sphere;
    };

    const renderer = rendererRef.current;

    // Helper: add a dashed Line2 connecting constraint/tendon markers
    const addHoverLine = (points, color, key, extraData = {}) => {
      const line = makeDashedLine2(points, color, renderer, dashedLineWidth);
      line.renderOrder = 9996;
      line.visible = true;
      line.userData = {
        isConstraintVisualization: true,
        isConstraintMarkerLine: true,
        isDashedLine2: true,
        key,
        ...extraData,
      };
      group.add(line);
      return line;
    };

    // Helper: update a Line2's geometry from a THREE.Vector3 array
    const updateLine2Points = (line, points) => {
      const flat = [];
      for (const p of points) flat.push(p.x, p.y, p.z);
      line.geometry.setPositions(flat);
      line.computeLineDistances();
    };

    const addConstraintMarkerNameLabel = (parentObject, key, rawName, options = {}) => {
      if (!parentObject || key == null) return;
      const { showTendonActuatorIcon = false } = options;
      const s = rawName != null ? String(rawName) : "";
      const i = s.lastIndexOf("/");
      const shortName = i >= 0 ? s.slice(i + 1) : s;
      const text = shortName || String(key);
      const onLabelClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!constraintMarkersGroupRef.current?.visible) return;
        applyConstraintMarkerKeySelection(key);
      };

      let labelDiv;
      if (showTendonActuatorIcon) {
        labelDiv = document.createElement("div");
        labelDiv.className = CONSTRAINT_LABEL_CLASS;
        applyLabelStyles(labelDiv);
        applyJointLabelLayoutStyles(labelDiv);

        const iconWrap = document.createElement("span");
        iconWrap.className = JOINT_LABEL_ACTUATOR_ICON_CLASS;
        iconWrap.setAttribute("aria-hidden", "true");
        iconWrap.style.setProperty("display", "inline-flex", "important");
        iconWrap.style.setProperty("color", ACTUATED_JOINT_LABEL_ICON_COLOR, "important");
        iconWrap.style.setProperty("align-items", "center", "important");
        iconWrap.style.setProperty("justify-content", "center", "important");
        iconWrap.style.setProperty("flex-shrink", "0", "important");
        iconWrap.style.setProperty("margin-inline-end", "4px", "important");
        iconWrap.style.lineHeight = "0";
        iconWrap.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14a1 1 0 0 1-.78-1.63l9.4-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.4 10.2a.5.5 0 0 1-.86-.46l-1.92-6.02A1 1 0 0 0 11 14z"/></svg>';

        const textSpan = document.createElement("span");
        textSpan.className = JOINT_LABEL_TEXT_CLASS;
        textSpan.textContent = text;

        labelDiv.appendChild(iconWrap);
        labelDiv.appendChild(textSpan);
        labelDiv.addEventListener("click", onLabelClick);
        labelDiv.cleanupManagedLabel = () => labelDiv.removeEventListener("click", onLabelClick);
      } else {
        labelDiv = createManagedLabelElement({
          className: CONSTRAINT_LABEL_CLASS,
          text,
          onClick: onLabelClick,
        });
      }

      const label = new CSS2DObject(labelDiv);
      applyMarkerLabelCss2dLayout(label);
      label.position.set(0, CONSTRAINT_MARKER_LABEL_LIFT_LOCAL_METERS, 0);
      label.userData.constraintMarkerKey = key;
      label.userData.stackSpreadSortKey = `constraint:${String(key)}`;
      label.userData.stackSpreadBaseLocal = new THREE.Vector3(
        0,
        CONSTRAINT_MARKER_LABEL_LIFT_LOCAL_METERS,
        0
      );
      parentObject.add(label);
    };

    // --- Connect-body equalities ---
    visibleEqualitiesForMarkers.forEach((eq) => {
      if (eq.type !== "connect" || eq.objtype !== "body") return;
      if (!eq.obj1 || !eq.obj2) return;
      const body1Obj = sceneObjectsRef.current[eq.obj1];
      const body2Obj = sceneObjectsRef.current[eq.obj2];
      if (!body1Obj || !body2Obj) return;

      const anchor = Array.isArray(eq.data) && eq.data.length >= 3 ? eq.data : [0, 0, 0];
      const anchorState = getConnectBodyAnchorState(body1Obj, body2Obj, anchor);
      const p1 = anchorState.body1World;
      const p2 = anchorState.body2World;
      const connectUserData = {
        body1: body1Obj,
        body2: body2Obj,
        body1Anchor: anchorState.body1Anchor,
        body2Anchor: anchorState.body2Anchor,
      };

      const s1Connect = addMarkerSphere(p1, CONSTRAINT_CONNECT_BODY1_MARKER_COLOR, eq.uuid, {
        ...connectUserData,
        connectEndpoint: "body1",
      });
      addWireMarkerSphere(p2, CONSTRAINT_CONNECT_BODY2_MARKER_COLOR, eq.uuid, {
        ...connectUserData,
        connectEndpoint: "body2",
        markerSphereGeometry: markerGeoLargeSparseWire,
        markerRenderOrder: MARKER_SPHERE_RENDER_BASE + 1,
      });
      addConstraintMarkerNameLabel(s1Connect, eq.uuid, getConstraintDisplayName(eq, allEqualities));
      const displayLine = getConnectBodyDisplayLinePoints(connectUserData, {
        body1World: p1,
        body2World: p2,
      });
      const linePoints = displayLine?.points || [p1, p2];
      const line = addHoverLine(linePoints, CONNECT_COLOR, eq.uuid, connectUserData);
      line.visible =
        linePoints[0].distanceTo(linePoints[1]) > CONNECT_CONSTRAINT_GAP_VISIBLE_METERS;
    });

    // --- Weld-body equalities ---
    // Weld markers: amber wireframe box at the midpoint between the two bodies.
    // Pick proxy: invisible sphere co-located with the box.
    // Hover line: body-origin to body-origin.
    visibleEqualitiesForMarkers.forEach((eq) => {
      if (eq.type !== "weld" || eq.objtype !== "body") return;
      if (!eq.obj1 || !eq.obj2) return;
      const body1Obj = sceneObjectsRef.current[eq.obj1];
      const body2Obj = sceneObjectsRef.current[eq.obj2];
      if (!body1Obj || !body2Obj) return;

      body1Obj.updateMatrixWorld(true);
      body2Obj.updateMatrixWorld(true);
      const o1 = new THREE.Vector3().setFromMatrixPosition(body1Obj.matrixWorld);
      const o2 = new THREE.Vector3().setFromMatrixPosition(body2Obj.matrixWorld);
      const mid = new THREE.Vector3().lerpVectors(o1, o2, 0.5);

      // Wireframe box — visual indicator of rigidity
      const weldBox = new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.BoxGeometry(weldBoxSize, weldBoxSize, weldBoxSize)),
        new THREE.LineBasicMaterial({
          color: WELD_COLOR,
          depthTest: false,
          depthWrite: false,
          linewidth: Math.max(1, lineWidthScale),
        })
      );
      weldBox.position.copy(mid);
      weldBox.renderOrder = 9996;
      weldBox.userData = {
        isConstraintVisualization: true,
        isWeldBox: true,
        key: eq.uuid,
        body1: body1Obj,
        body2: body2Obj,
      };
      group.add(weldBox);

      // Invisible sphere for raycasting (same world position as box)
      const pickSphere = new THREE.Mesh(
        weldPickGeo,
        new THREE.MeshBasicMaterial({
          transparent: true,
          opacity: 0,
          depthTest: false,
          depthWrite: false,
        })
      );
      pickSphere.position.copy(mid);
      pickSphere.renderOrder = 9996;
      pickSphere.userData = {
        isConstraintVisualization: true,
        isConstraintMarker: true,
        key: eq.uuid,
        isWeldPick: true,
        body1: body1Obj,
        body2: body2Obj,
      };
      group.add(pickSphere);
      constraintMarkerMeshesRef.current.push(pickSphere);
      addConstraintMarkerNameLabel(
        pickSphere,
        eq.uuid,
        getConstraintDisplayName(eq, allEqualities)
      );

      // Hover line: body-origin to body-origin
      addHoverLine([o1, o2], WELD_COLOR, eq.uuid, {
        body1: body1Obj,
        body2: body2Obj,
        useBodyOrigins: true,
      });
    });

    // --- Joint equalities ---
    // Markers at each joint center; solid arc between the joints.
    visibleEqualitiesForMarkers.forEach((eq) => {
      if (eq.type !== "joint") return;
      if (!eq.obj1 || !eq.obj2) return;
      const vg1 = findJointVizGroupInScene(sceneRef.current, eq.obj1);
      const vg2 = findJointVizGroupInScene(sceneRef.current, eq.obj2);
      if (!vg1 || !vg2) return;

      const key = eq.uuid;
      const p1 = new THREE.Vector3();
      const p2 = new THREE.Vector3();
      setJointPivotWorldFromViz(p1, vg1, jointObjectsRef);
      setJointPivotWorldFromViz(p2, vg2, jointObjectsRef);

      const jointEqCoincident = coincidentTendonJointEqPairs.has(
        canonicalJointPairKey(eq.obj1, eq.obj2)
      );
      const eqSphereGeom = jointEqCoincident ? markerGeoLarge : markerGeo;
      const eqSphereRenderOrder = jointEqCoincident
        ? MARKER_SPHERE_RENDER_LARGE_BACK
        : MARKER_SPHERE_RENDER_BASE;

      const s1JointEq = addMarkerSphere(p1, CONSTRAINT_JOINT_EQ_MARKER_COLOR, key, {
        vizGroup: vg1,
        markerSphereGeometry: eqSphereGeom,
        markerRenderOrder: eqSphereRenderOrder,
      });
      addMarkerSphere(p2, CONSTRAINT_JOINT_EQ_MARKER_COLOR, key, {
        vizGroup: vg2,
        markerSphereGeometry: eqSphereGeom,
        markerRenderOrder: eqSphereRenderOrder,
      });
      addConstraintMarkerNameLabel(s1JointEq, key, getConstraintDisplayName(eq, allEqualities));

      // Arc line — coincident tendon pair: shallower bow same direction as tendon (stronger bow).
      const eqArcBowScale = jointEqCoincident ? COINCIDENT_JOINT_EQ_ARC_BOW_SCALE : 1;
      const initCurve = new THREE.CatmullRomCurve3(buildTendonCurvePoints([p1, p2], eqArcBowScale));
      const arcLine = makeSolidLine2(
        initCurve.getPoints(48),
        CONSTRAINT_JOINT_EQ_MARKER_COLOR,
        renderer,
        jointEqLineWidth
      );
      arcLine.renderOrder = jointEqCoincident
        ? MARKER_ARC_RENDER_JOINT_EQ_COINCIDENT_BACK
        : MARKER_ARC_RENDER_NORMAL;
      arcLine.visible = true;
      arcLine.userData = {
        isConstraintVisualization: true,
        isConstraintMarkerLine: true,
        isDashedLine2: true,
        key,
        vizGroup1: vg1,
        vizGroup2: vg2,
        isJointEqArc: true,
        arcBowScale: eqArcBowScale,
      };
      group.add(arcLine);
      constraintMarkerMeshesRef.current.push(arcLine);
    });

    // --- Tendons ---
    const routeObjectIndex = buildViewerSceneRouteObjectIndex(sceneRef.current);
    visibleTendonsForMarkers.forEach((tendon, idx) => {
      const fallbackColor = TENDON_COLORS[idx % TENDON_COLORS.length];
      const tendonStyle = getTendonVisualizationStyle(
        tendon,
        fallbackColor,
        solidLineWidth,
        0.92,
        constraintMarkerScales.tendonParsedAppearance
      );
      const color = tendonStyle.color;
      const key = `tendon:${tendon.uuid}`;

      if (tendon.kind === "spatial") {
        const routeObjects = collectSpatialTendonRouteObjects(
          sceneRef.current,
          tendon,
          routeObjectIndex
        );
        if (routeObjects.length < 2) return;
        const routeData = routeObjects.map((routeObj) => {
          const pos = new THREE.Vector3();
          routeObj.getWorldPosition(pos);
          return { routeObj, pos };
        });

        let firstTendonSphere = null;
        routeData.forEach(({ routeObj, pos }) => {
          const s = addMarkerSphere(pos, color, key, { tendonRouteObject: routeObj });
          if (!firstTendonSphere) firstTendonSphere = s;
        });
        addConstraintMarkerNameLabel(firstTendonSphere, key, tendon.name);

        const line = makeSolidLine2(
          routeData.map((d) => d.pos),
          color,
          renderer,
          tendonStyle.lineWidth,
          tendonStyle.opacity
        );
        line.renderOrder = MARKER_ARC_RENDER_NORMAL;
        line.visible = true;
        line.userData = {
          isConstraintVisualization: true,
          isConstraintMarkerLine: true,
          isDashedLine2: true,
          key,
          tendonRouteObjects: routeData.map((d) => d.routeObj),
        };
        group.add(line);
        constraintMarkerMeshesRef.current.push(line);
        return;
      }

      const jointRefs = (tendon.joints || []).filter((j) => j?.joint);
      if (jointRefs.length < 2) return;

      const jointData = jointRefs
        .map(({ joint: uuid }) => {
          const vg = findJointVizGroupInScene(sceneRef.current, uuid);
          if (!vg) return null;
          const pos = new THREE.Vector3();
          setJointPivotWorldFromViz(pos, vg, jointObjectsRef);
          return { vg, pos };
        })
        .filter(Boolean);

      if (jointData.length < 2) return;

      const tendonMemberIds = jointRefs.map((j) => j.joint).filter(Boolean);
      const tendonCoincident =
        tendonMemberIds.length === 2 &&
        coincidentTendonJointEqPairs.has(
          canonicalJointPairKey(tendonMemberIds[0], tendonMemberIds[1])
        );

      let firstTendonSphere = null;
      jointData.forEach(({ vg, pos }) => {
        const s = addMarkerSphere(pos, color, key, {
          vizGroup: vg,
          ...(tendonCoincident
            ? {
                markerSphereGeometry: markerGeoSmall,
                markerRenderOrder: MARKER_SPHERE_RENDER_SMALL_TOP,
              }
            : {}),
        });
        if (!firstTendonSphere) firstTendonSphere = s;
      });
      addConstraintMarkerNameLabel(firstTendonSphere, key, tendon.name);

      const positions = jointData.map((d) => d.pos);
      const tendonArcBowScale = tendonCoincident ? COINCIDENT_TENDON_ARC_BOW_SCALE : 1;
      const arcPts = new THREE.CatmullRomCurve3(
        buildTendonCurvePoints(positions, tendonArcBowScale)
      ).getPoints(48);
      const arcLine = makeSolidLine2(
        arcPts,
        color,
        renderer,
        tendonStyle.lineWidth,
        tendonStyle.opacity
      );
      arcLine.renderOrder = tendonCoincident
        ? MARKER_ARC_RENDER_TENDON_OVER_EQ
        : MARKER_ARC_RENDER_NORMAL;
      arcLine.visible = true;
      arcLine.userData = {
        isConstraintVisualization: true,
        isConstraintMarkerLine: true,
        isDashedLine2: true,
        key,
        tendonVizGroups: jointData.map((d) => d.vg),
        arcBowScale: tendonArcBowScale,
      };
      group.add(arcLine);
      constraintMarkerMeshesRef.current.push(arcLine);
    });

    const hasMarkerSources =
      Object.keys(allEqualities || {}).length > 0 || Object.keys(allTendons || {}).length > 0;
    if (group.children.length > 0) {
      constraintMarkerBuildRetryCountRef.current = 0;
    } else if (hasMarkerSources && constraintMarkerBuildRetryCountRef.current < 20) {
      constraintMarkerBuildRetryCountRef.current += 1;
      retryTimeout = setTimeout(() => {
        setConstraintMarkersRetryNonce((nonce) => nonce + 1);
      }, 100);
    }

    // When global markers are off but a selected constraint forced a build, immediately hide
    // all non-selected 3D marker objects so they don't flash visible before the visibility pass runs.
    if (!anyConstraintMarkerLayerVisible) {
      group.traverse((obj) => {
        if (!obj.userData?.isConstraintVisualization) return;
        const key = obj.userData.key;
        if (key == null) return;
        const keyStr = String(key);
        const isSelected = keyStr.startsWith("tendon:")
          ? keyStr.slice("tendon:".length) === selectedTendon
          : keyStr === selectedEquality;
        if (!isSelected) obj.visible = false;
      });
    }

    // New CSS2D labels default to visible=true; rebuilding for actuator/color updates must not
    // resurrect labels when labelsOff is on — sync immediately from current inspector state.
    syncConstraintMarkerCss2dLabelVisibility(group, {
      showConstraintMarkers,
      constraintVisibilityUi,
      selectedEquality,
      selectedTendon,
      focusModeActive,
      focusBodyUuid,
      focusMeshUuid,
      focusCollisionGeomUuid,
      selectionFocusBodiesMulti: selectionFocusBodiesMultiSnapshot(focusModeActive),
    });

    // Animation: update positions each frame + hover detection
    const hoverRaycaster = new THREE.Raycaster();
    const _scratch = new THREE.Vector3();
    const sameMarkerId = (a, b) => a != null && b != null && String(a) === String(b);
    const markerLayerAllowsKey = (markerKey) => {
      const snapshot = constraintMarkerLayerStateRef.current;
      if (markerKey == null) {
        return (
          !!snapshot.showConstraintMarkers &&
          (snapshot.constraintsVisible !== false || snapshot.tendonsVisible !== false)
        );
      }
      const keyStr = String(markerKey);
      let matchesSelection;
      if (keyStr.startsWith("tendon:")) {
        const tendonLayerVisible =
          snapshot.showConstraintMarkers && snapshot.tendonsVisible !== false;
        const tendonUuid = keyStr.slice("tendon:".length);
        if ((snapshot.tendonMarkerVisibility[tendonUuid] ?? true) === false) return false;
        matchesSelection =
          typeof snapshot.selectedTendon === "string" &&
          sameMarkerId(tendonUuid, snapshot.selectedTendon);
        if (snapshot.constraintFocus) {
          return (tendonLayerVisible || matchesSelection) && matchesSelection;
        }
        return tendonLayerVisible || matchesSelection;
      } else {
        const constraintLayerVisible =
          snapshot.showConstraintMarkers && snapshot.constraintsVisible !== false;
        if ((snapshot.equalityMarkerVisibility[keyStr] ?? true) === false) return false;
        matchesSelection =
          typeof snapshot.selectedEquality === "string" &&
          sameMarkerId(keyStr, snapshot.selectedEquality);
        if (snapshot.constraintFocus) {
          return (constraintLayerVisible || matchesSelection) && matchesSelection;
        }
        return constraintLayerVisible || matchesSelection;
      }
    };
    const tick = () => {
      const g = constraintMarkersGroupRef.current;
      if (g && g.visible) {
        g.children.forEach((obj) => {
          const ud = obj.userData;
          if (!ud) return;

          // --- position updates ---
          if (ud.isConstraintMarker) {
            if (ud.connectEndpoint && ud.body1 && ud.body2) {
              const positions = getConnectBodyAnchorWorldPositions(ud);
              if (positions) {
                obj.position.copy(
                  ud.connectEndpoint === "body2" ? positions.body2World : positions.body1World
                );
              }
            } else if (ud.isWeldPick && ud.body1 && ud.body2) {
              // weld pick sphere: midpoint of body origins
              const o1 = _scratch.setFromMatrixPosition(ud.body1.matrixWorld).clone();
              const o2 = new THREE.Vector3().setFromMatrixPosition(ud.body2.matrixWorld);
              obj.position.lerpVectors(o1, o2, 0.5);
            } else if (ud.vizGroup) {
              // tendon / joint-equality: pivot tracks poseGroup (vizGroup alone does not during physics)
              setJointPivotWorldFromViz(obj.position, ud.vizGroup, jointObjectsRef);
            } else if (ud.tendonRouteObject) {
              ud.tendonRouteObject.getWorldPosition(obj.position);
            }
          }

          if (ud.isWeldBox && ud.body1 && ud.body2) {
            const o1 = _scratch.setFromMatrixPosition(ud.body1.matrixWorld).clone();
            const o2 = new THREE.Vector3().setFromMatrixPosition(ud.body2.matrixWorld);
            obj.position.lerpVectors(o1, o2, 0.5);
          }

          // --- visible line updates ---
          if (ud.isConstraintMarkerLine) {
            const lineLayerVisible = markerLayerAllowsKey(ud.key);
            let newPts = null;
            if (ud.body1Anchor && ud.body2Anchor && ud.body1 && ud.body2) {
              const positions = getConnectBodyAnchorWorldPositions(ud);
              if (positions) {
                const displayLine = getConnectBodyDisplayLinePoints(ud, positions);
                const points = displayLine?.points || [positions.body1World, positions.body2World];
                const gap =
                  displayLine?.gap ?? positions.body1World.distanceTo(positions.body2World);
                obj.visible =
                  lineLayerVisible &&
                  points[0].distanceTo(points[1]) > CONNECT_CONSTRAINT_GAP_VISIBLE_METERS;
                const t = displayLine?.isAuthoredSpan
                  ? 0
                  : Math.min(gap / CONNECT_CONSTRAINT_GAP_WARN_METERS, 1.0);
                obj.material.color.lerpColors(connectGapOkColor, connectGapWarnColor, t);
                newPts = points;
              }
            } else if (ud.useBodyOrigins && ud.body1 && ud.body2) {
              newPts = [
                new THREE.Vector3().setFromMatrixPosition(ud.body1.matrixWorld),
                new THREE.Vector3().setFromMatrixPosition(ud.body2.matrixWorld),
              ];
            } else if (ud.isJointEqArc && ud.vizGroup1 && ud.vizGroup2) {
              const jp1 = new THREE.Vector3();
              const jp2 = new THREE.Vector3();
              setJointPivotWorldFromViz(jp1, ud.vizGroup1, jointObjectsRef);
              setJointPivotWorldFromViz(jp2, ud.vizGroup2, jointObjectsRef);
              const bowScale = ud.arcBowScale ?? 1;
              newPts = new THREE.CatmullRomCurve3(
                buildTendonCurvePoints([jp1, jp2], bowScale)
              ).getPoints(48);
            } else if (ud.tendonVizGroups) {
              const positions = ud.tendonVizGroups.map((vg) => {
                const p = new THREE.Vector3();
                setJointPivotWorldFromViz(p, vg, jointObjectsRef);
                return p;
              });
              if (positions.length >= 2) {
                const bowScale = ud.arcBowScale ?? 1;
                newPts = new THREE.CatmullRomCurve3(
                  buildTendonCurvePoints(positions, bowScale)
                ).getPoints(48);
              }
            } else if (ud.tendonRouteObjects) {
              newPts = ud.tendonRouteObjects.map((routeObj) => {
                const p = new THREE.Vector3();
                routeObj.getWorldPosition(p);
                return p;
              });
            }
            if (!lineLayerVisible) {
              obj.visible = false;
            }
            if (newPts) {
              if (ud.isDashedLine2) {
                updateLine2Points(obj, newPts);
              } else {
                obj.geometry.setFromPoints(newPts);
                obj.geometry.attributes.position.needsUpdate = true;
              }
            }
          }
        });

        // Hover: raycast against marker spheres to track which item the pointer is over
        const markers = constraintMarkerMeshesRef.current;
        if (markers.length > 0 && cameraRef.current) {
          hoverRaycaster.setFromCamera(constraintPointerRef.current, cameraRef.current);
          const hits = hoverRaycaster.intersectObjects(markers, false);
          const chosen = preferTendonConstraintMarkerHit(hits);
          const newKey = chosen?.object?.userData?.key ?? null;
          constraintHoveredKeyRef.current = newKey;
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      if (retryTimeout) clearTimeout(retryTimeout);
      if (rafId !== null) cancelAnimationFrame(rafId);
      constraintHoveredKeyRef.current = null;
      constraintMarkerMeshesRef.current = [];
      clearGroupChildren();
      const g = constraintMarkersGroupRef.current;
      if (g && g.parent) g.parent.remove(g);
      constraintMarkersGroupRef.current = null;
    };
  }, [
    showConstraintMarkers,
    constraintVisibilityUi.constraintsVisible,
    constraintVisibilityUi.tendonsVisible,
    constraintMarkerLayerVisible,
    tendonMarkerLayerVisible,
    selectedEquality,
    selectedTendon,
    allEqualities,
    allTendons,
    constraintMarkersRetryNonce,
    constraintMarkerSettings,
    structureChangeTick,
    sceneRef,
    sceneObjectsRef,
    cameraRef,
    rendererRef,
    jointObjectsRef,
    constraintMarkersGroupRef,
    constraintMarkerMeshesRef,
    constraintHoveredKeyRef,
    constraintMarkerLayerStateRef,
    constraintPointerRef,
    constraintMarkerBuildRetryCountRef,
    constraintVisibilityUi,
    focusModeActive,
    focusBodyUuid,
    focusMeshUuid,
    focusCollisionGeomUuid,
    setConstraintMarkersRetryNonce,
  ]);

  // Marker effect above runs *after* the main visibility effect and recreates CSS2D labels defaulting to
  // visible=true. Re-apply constraint label rules here whenever markers or inspector visibility change.
  useEffect(() => {
    syncConstraintMarkerCss2dLabelVisibility(constraintMarkersGroupRef.current, {
      showConstraintMarkers,
      constraintVisibilityUi,
      selectedEquality,
      selectedTendon,
      focusModeActive,
      focusBodyUuid,
      focusMeshUuid,
      focusCollisionGeomUuid,
      selectionFocusBodiesMulti: selectionFocusBodiesMultiSnapshot(focusModeActive),
    });
  }, [
    showConstraintMarkers,
    constraintVisibilityUi.focus,
    constraintVisibilityUi.labelsOff,
    constraintVisibilityUi.tendonLabelsOff,
    constraintVisibilityUi.constraintsVisible,
    constraintVisibilityUi.tendonsVisible,
    selectedEquality,
    selectedTendon,
    focusModeActive,
    focusBodyUuid,
    focusMeshUuid,
    focusCollisionGeomUuid,
    selectedBodiesMultiKey,
    allEqualities,
    allTendons,
    constraintMarkersGroupRef,
    constraintVisibilityUi,
    constraintMarkersRetryNonce,
    structureChangeTick,
  ]);
}
